import React, { useEffect, useState } from "react";
import { Box, Typography, Chip } from "@mui/material";
import CustomDataGrid from "../../Components/CustomDataGrid";
import { REACT_APP_BACKEND_URL } from "../../Utils/Language/urlBase";
import { toast } from "react-toastify";
import useAuthHeader from "react-auth-kit/hooks/useAuthHeader";
import WarningIcon from "@mui/icons-material/Warning";

const ItemsExpiringSoon = () => {
    const [expiringItems, setExpiringItems] = useState([]);
    const authHeader = useAuthHeader();

    const fetchItems = async () => {
        try {
            const response = await fetch(`${REACT_APP_BACKEND_URL}/items`, {
                method: "GET",
                headers: {
                    Authorization: authHeader,
                    "Content-Type": "application/json",
                },
            });
            if (response.ok) {
                const responseData = await response.json();
                const items = responseData.data;

                // Calculate date 2 months from now
                const twoMonthsFromNow = new Date();
                twoMonthsFromNow.setMonth(twoMonthsFromNow.getMonth() + 2);

                // Filter items with stock that has expiry date within 2 months and remaining quantity > 0
                const expiringStockItems = [];

                items.forEach((item) => {
                    if (item.Stock && Array.isArray(item.Stock)) {
                        item.Stock.forEach((stock) => {
                            if (
                                stock.quantityRemaining > 0 &&
                                stock.expiryDate
                            ) {
                                const expiryDate = new Date(stock.expiryDate);
                                const today = new Date();

                                // Check if expiry date is within the next 2 months
                                if (
                                    expiryDate <= twoMonthsFromNow &&
                                    expiryDate >= today
                                ) {
                                    const daysUntilExpiry = Math.ceil(
                                        (expiryDate - today) /
                                            (1000 * 60 * 60 * 24)
                                    );

                                    expiringStockItems.push({
                                        id: `${item.id}-${stock.id}`,
                                        itemId: item.id,
                                        stockId: stock.id,
                                        name: item.name,
                                        barcode: item.barcode,
                                        description: item.description,
                                        quantityRemaining:
                                            stock.quantityRemaining,
                                        quantityInitial: stock.quantityInitial,
                                        pricePurchase: stock.pricePurchase,
                                        expiryDate: stock.expiryDate,
                                        daysUntilExpiry: daysUntilExpiry,
                                        stockLocationName:
                                            stock.stockLocation?.name || "N/A",
                                        otherPartyName:
                                            stock.otherParty?.name || "N/A",
                                        addedBy: stock.addedBy?.name || "N/A",
                                    });
                                }
                            }
                        });
                    }
                });

                // Sort by days until expiry (closest expiry first)
                expiringStockItems.sort(
                    (a, b) => a.daysUntilExpiry - b.daysUntilExpiry
                );

                setExpiringItems(expiringStockItems);
            } else {
                throw new Error("Failed to fetch items.");
            }
        } catch (error) {
            toast.error(error.message || "Error fetching items.");
        }
    };

    useEffect(() => {
        fetchItems();
    }, [authHeader]);

    const columns = [
        {
            field: "name",
            headerName: "Item Name",
            minWidth: 200,
            flex: 1,
        },
        {
            field: "barcode",
            headerName: "Barcode",
            minWidth: 120,
            flex: 1,
        },
        {
            field: "quantityRemaining",
            headerName: "Qty Remaining",
            minWidth: 120,
            flex: 1,
        },
        {
            field: "pricePurchase",
            headerName: "Purchase Price",
            minWidth: 120,
            flex: 1,
            valueFormatter: (value) => `$${Number(value).toFixed(2)}`,
        },
        {
            field: "expiryDate",
            headerName: "Expiry Date",
            minWidth: 120,
            flex: 1,
            valueGetter: (expiryDate) => {
                return new Date(expiryDate).toLocaleDateString();
            },
        },
        {
            field: "daysUntilExpiry",
            headerName: "Days Until Expiry",
            minWidth: 150,
            flex: 1,
            renderCell: (params) => {
                const days = params.row.daysUntilExpiry;
                let color = "success";
                if (days <= 30) {
                    color = "error";
                } else if (days <= 45) {
                    color = "warning";
                }
                return (
                    <Chip
                        label={`${days} days`}
                        color={color}
                        size="small"
                        icon={days <= 30 ? <WarningIcon /> : undefined}
                    />
                );
            },
        },
        {
            field: "stockLocationName",
            headerName: "Stock Location",
            minWidth: 150,
            flex: 1,
        },
        {
            field: "otherPartyName",
            headerName: "Supplier",
            minWidth: 150,
            flex: 1,
        },
    ];

    return (
        <Box sx={{ width: "100%" }}>
            <Box sx={{ mb: 3 }}>
                <Typography
                    variant="h4"
                    sx={{ fontWeight: "bold", mb: 1 }}
                    color="primary"
                >
                    Items Expiring Soon (Within 2 Months)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Showing items with batches that expire within the next 2
                    months and have remaining quantity greater than 0
                </Typography>
                <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
                    <Chip
                        label={`Total Batches: ${expiringItems.length}`}
                        color="primary"
                        variant="outlined"
                    />
                    <Chip
                        label={`Critical (≤30 days): ${
                            expiringItems.filter(
                                (item) => item.daysUntilExpiry <= 30
                            ).length
                        }`}
                        color="error"
                        variant="outlined"
                    />
                    <Chip
                        label={`Warning (31-45 days): ${
                            expiringItems.filter(
                                (item) =>
                                    item.daysUntilExpiry > 30 &&
                                    item.daysUntilExpiry <= 45
                            ).length
                        }`}
                        color="warning"
                        variant="outlined"
                    />
                </Box>
            </Box>
            <CustomDataGrid
                columns={columns}
                rows={expiringItems}
                perPage={20}
            />
        </Box>
    );
};

export default ItemsExpiringSoon;
